import React, { useState } from 'react';
import { Remision } from 'types/remisiones';
import { CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon } from '@heroicons/react/24/outline';
import { STORE_STYLES } from '../../../assets/data/arrays';

interface RemisionesTableProps {
  remisiones: Remision[];
  isLoading: boolean;
  onViewProducts?: (remision: Remision) => void;
}

const RemisionesTable: React.FC<RemisionesTableProps> = ({ remisiones, isLoading, onViewProducts }) => {
  const [sortField, setSortField] = useState<'Numero' | 'Fecha'>('Numero');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: 'Numero' | 'Fecha') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const sortedRemisiones = [...remisiones].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    if (sortField === 'Numero') {
      aValue = a.Numero;
      bValue = b.Numero;
    } else {
      aValue = a.Fecha ? new Date(a.Fecha).getTime() : 0;
      bValue = b.Fecha ? new Date(b.Fecha).getTime() : 0;
    }

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 border-t"></div>
          ))}
        </div>
      </div>
    );
  }

  if (remisiones.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center mb-6">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay remisiones</h3>
        <p className="text-gray-500">No se encontraron remisiones para el SKU consultado</p>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('Numero')}
              >
                <div className="flex items-center space-x-1">
                  <span>Número</span>
                  {sortField === 'Numero' && (
                    sortDirection === 'desc' ? 
                      <ChevronDownIcon className="h-4 w-4" /> : 
                      <ChevronUpIcon className="h-4 w-4" />
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tienda
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('Fecha')}
              >
                <div className="flex items-center space-x-1">
                  <span>Fecha</span>
                  {sortField === 'Fecha' && (
                    sortDirection === 'desc' ? 
                      <ChevronDownIcon className="h-4 w-4" /> : 
                      <ChevronUpIcon className="h-4 w-4" />
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                # RE
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRemisiones.map((remision) => (
              <tr key={remision.Numero} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {remision.Numero}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    {(() => {
                      const store = STORE_STYLES.find(s => s.shortValue === remision.Prefijo);
                      
                      if (!store) {
                        return <span className="text-gray-500">{remision.Prefijo || 'N/A'}</span>;
                      }
                      
                      return <span className={store.color}>{store.shortValue}</span>;
                    })()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {remision.IdCliente}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {remision.Fecha ? formatDate(remision.Fecha) : 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(remision.ValorTotal)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {remision.Procesado ? (
                      <>
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />
                        <span className="text-sm text-green-600 font-medium">Procesado</span>
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-1" />
                        <span className="text-sm text-red-600 font-medium">Pendiente</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {remision.NumeroOfima || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onViewProducts?.(remision)}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Ver Productos
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RemisionesTable;
